import React, { useCallback, useMemo, useState } from 'react';

import filesize from 'filesize';
import { AnimatePresence, motion } from 'framer-motion';
import { CloudDownloadIcon, DownloadIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';
import { Spinner } from '@/shadcn/spinner';

import type {
  DataImporterConfig,
  DataImporterData,
  ImporterFileData
} from '@/components/ui/dataImporter/dataImporter.type';
import {
  buildTableConfigFromColumnNames,
  buildTableConfigFromMapping
} from '@/components/ui/dataImporter/utils';
import { KeyValue } from '@/components/ui/keyValue/keyValue';
import Table from '@/components/ui/table';
import { formatNumber } from '@/utils';
import { downloadFile } from '@/utils/downloadFile';

type Props = {
  dataImporter: DataImporterConfig;
  data: DataImporterData;
};

// data importer reader component
export const DataImporterReader = React.memo(
  ({ dataImporter, data }: Props) => {
    // state displaying the table data modal
    const [showModal, setShowModal] = useState(false);
    const toggleShowModal = useCallback(
      () => setShowModal(!showModal),
      [showModal]
    );

    // file downloading state
    const [loading, setLoading] = useState(false);

    const {
      importedDataKey = 'rows',
      importedDataTitle = 'Imported Data',
      fileKey = 'file',
      table: tableConfig,
      mapping
    } = dataImporter;

    const importedData: Record<string, unknown>[] = data[importedDataKey];
    const file: ImporterFileData | undefined = data[fileKey];

    // if table modal is shown and no table config has been declared in the action schema
    // then build it from imported data (or mapping) as in dataImporter
    const usedTableConfig = useMemo(() => {
      if (!tableConfig) {
        if (mapping) {
          // use the mapping to build the table config
          return buildTableConfigFromMapping(mapping);
        }
        // just use the column names to build it
        let foundKeys: string[] = [];
        (importedData || []).forEach(row => {
          foundKeys = foundKeys.concat(
            Object.keys(row).filter(key => !foundKeys.includes(key))
          );
        });
        return buildTableConfigFromColumnNames(foundKeys);
      }
      return tableConfig;
    }, [tableConfig, mapping, importedData]);

    const nbOfLines = (importedData || []).length;
    const nbImportsMsg = `${formatNumber(nbOfLines)} rows ${nbOfLines !== 1 ? 'have' : 'has'} been imported`;
    const fileExtension = file?.name?.split('.').pop()?.toUpperCase();
    return (
      <KeyValue dataCyName="data-importer" title={importedDataTitle}>
        <div className="mt-2 flex w-full flex-col gap-1">
          <Dialog>
            <DialogTrigger asChild>
              <div
                className="mb-2 flex w-full cursor-pointer flex-row flex-nowrap items-center gap-4 rounded-md border border-border bg-accent px-4 py-2 text-sm hover:brightness-98 dark:hover:brightness-110"
                data-cy="data-importer-view-changes"
                onClick={toggleShowModal}
              >
                <CloudDownloadIcon
                  className="text-accent-foreground"
                  size={22}
                />
                <section>
                  <div>View imported table</div>
                  <div
                    className="text-muted-foreground text-xs"
                    data-cy="data-importer-nb-imports"
                  >
                    {nbImportsMsg}
                  </div>
                </section>
              </div>
            </DialogTrigger>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-none">
              <DialogHeader>
                <DialogTitle>
                  <div className="flex flex-row flex-nowrap items-center gap-4 text-lg">
                    <div>{importedDataTitle}</div>
                    <div className="font-normal text-muted-foreground text-xs">
                      {nbImportsMsg}
                    </div>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="overflow-y-auto text-[13px] text-foreground leading-[1.25]">
                <div className="size-full overflow-auto">
                  <div
                    className="flex h-[80vh] w-full flex-col flex-nowrap items-center"
                    data-cy="data-importer-table"
                  >
                    <Table config={usedTableConfig} data={importedData || []} />
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          {file && (
            <div className="flex shrink-0 flex-row flex-nowrap text-muted-foreground text-xs">
              <div
                className="flex cursor-pointer flex-row items-center gap-2 underline hover:text-primary"
                data-cy="data-importer-download-file-button"
                data-is-loading={loading}
                onClick={() => downloadFile(file, setLoading, loading)}
              >
                <AnimatePresence initial={false} mode="wait">
                  {loading ? (
                    <motion.span
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      className="inline-flex"
                      exit={{ opacity: 0, scale: 0.9, filter: 'blur(1px)' }}
                      initial={{ opacity: 0, scale: 0.9, filter: 'blur(1px)' }}
                      key="spinner-icon"
                      transition={{ duration: 0.2 }}
                    >
                      <Spinner className="size-4" />
                    </motion.span>
                  ) : (
                    <motion.span
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      className="inline-flex"
                      exit={{ opacity: 0, scale: 0.9, filter: 'blur(1px)' }}
                      initial={{ opacity: 0, scale: 0.9, filter: 'blur(1px)' }}
                      key="download-icon"
                      transition={{ duration: 0.2 }}
                    >
                      <DownloadIcon className="size-4" />
                    </motion.span>
                  )}
                </AnimatePresence>

                <div data-cy="data-importer-download-file-text">
                  Download imported {fileExtension} file
                </div>
              </div>
              <div className="mx-2 text-[10px] leading-[16px]">
                ({filesize(file.size, { round: 0 })})
              </div>
            </div>
          )}
        </div>
      </KeyValue>
    );
  }
);
